<script lang="ts">
	import { ClientVelatlasLoggerApi } from '$lib/api/velatlas/velatlas.loggerApi.client';
	import { OfficialLinkBtn, type OfficialLinkBtnProps } from '$lib/components/ui/OfficialLinks.svelte';
	import { WIFI_ACCESS_LABELS, type SimplePlace } from '$lib/features/place/place.type';
	import { ANALYTICS_EVENT_NAMES } from '$lib/statics';
	import { capitalizeWords } from '$lib/utils/general.util';
	import { generateOfficialLinks } from '$lib/utils/officialLinks.util';
	import type { ClassValue } from 'svelte/elements';
	import IconInformationOutline from '~icons/mdi/information-outline';
	import IconPlus from '~icons/mdi/plus';

	const {
		place,
		initiallyOpen = false,
		classes
	}: {
		place: SimplePlace | undefined | null;
		initiallyOpen?: boolean;
		classes?: ClassValue;
	} = $props();

	const analyticsApi = new ClientVelatlasLoggerApi();

	let detailsOpen = $derived<boolean>(initiallyOpen);
	const officialLinks = $derived<OfficialLinkBtnProps[]>(generateOfficialLinks(place));
	const hasOverviewAndStats = $derived<boolean>(
		place === undefined || place === null
			? false
			: place.inceptionYear !== null ||
					place.elevationMeters !== null ||
					place.exhibitionSize !== null ||
					place.visitorsPerYear !== null ||
					place.cost !== null ||
					place.wifiAccess !== null
	);
	const hasArchitectureAndHeritage = $derived<boolean>(
		place === undefined || place === null
			? false
			: (place.architecturalStyles !== null && place.architecturalStyles.length > 0) ||
					(place.architects !== null && place.architects.length > 0) ||
					(place.founders !== null && place.founders.length > 0) ||
					(place.heritageDesignations !== null && place.heritageDesignations.length > 0)
	);
	const hasWebAndAmenities = $derived<boolean>(
		place === undefined || place === null
			? false
			: officialLinks.length > 0 ||
					(place.facilities !== null && place.facilities.length > 0) ||
					(place.accessibilityProperties !== null && place.accessibilityProperties.length > 0)
	);
	const hasDetails = $derived<boolean>(hasOverviewAndStats || hasArchitectureAndHeritage || hasWebAndAmenities);

	function formatCurrency(amount: number, currencyCode: string): string {
		try {
			return new Intl.NumberFormat('en-GB', {
				style: 'currency',
				currency: currencyCode,
				minimumFractionDigits: 0,
				maximumFractionDigits: 2
			}).format(amount);
		} catch (error) {
			console.error(`Currency formatting failed for ${currencyCode}:`, error);
			return `${amount} ${currencyCode}`;
		}
	}

	$effect(() => {
		if (detailsOpen) {
			analyticsApi.sendAnalytics({ event: ANALYTICS_EVENT_NAMES.placeDetailsOpened });
		}
	});
</script>

{#if hasDetails}
	<details
		bind:open={detailsOpen}
		class="group shrink-0 overflow-hidden rounded-box border border-base-content/10 bg-base-300/50 transition-colors open:bg-base-200 hover:bg-base-300/75 {classes}"
	>
		<summary class="flex cursor-pointer items-center justify-between gap-2.5 p-4 select-none sm:p-5">
			<h3
				class="flex items-center gap-2 text-xs font-semibold tracking-wider text-base-content/90 transition-all duration-200 group-open:text-primary group-hover:text-primary sm:text-sm sm:tracking-widest"
			>
				<IconInformationOutline class="shrink-0 text-primary" />
				<span class="font-mono whitespace-nowrap">FULL PLACE DETAILS</span>
			</h3>
			<span
				class="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/75 text-primary-content transition-all duration-200 group-open:rotate-45 group-open:bg-primary group-open:text-primary-content group-hover:bg-primary"
			>
				<IconPlus />
			</span>
		</summary>

		<div class="border-t border-base-content/10 p-4 sm:p-5">
			{@render PlaceDetails(place!)}
		</div>
	</details>
{/if}

{#snippet PlaceDetails(place: SimplePlace)}
	<div class="grid grid-cols-[repeat(auto-fit,minmax(13rem,1fr))] gap-2">
		{#if hasOverviewAndStats}
			<div class="flex flex-col gap-3 rounded-box border border-base-content/5 bg-base-100/60 p-4 text-xs">
				<h4 class="font-bold tracking-wider text-base-content/70">OVERVIEW & STATS</h4>
				<dl class="flex flex-col gap-2">
					{@render OverviewAndStats()}
				</dl>
			</div>
		{/if}

		{#if hasArchitectureAndHeritage}
			<div class="flex flex-col gap-3 rounded-box border border-base-content/5 bg-base-100/60 p-4 text-xs">
				<h4 class="font-bold tracking-wider text-base-content/70">ARCHITECTURE & HERITAGE</h4>
				<dl class="flex flex-col gap-2">
					{@render ArchitectureAndHeritage()}
				</dl>
			</div>
		{/if}

		{#if hasWebAndAmenities}
			<div
				class="flex flex-col gap-3 rounded-box border border-base-content/5 bg-base-100/60 p-4 text-xs md:col-span-2 lg:col-span-1"
			>
				<h4 class="font-bold tracking-wider text-base-content/70">WEB & AMENITIES</h4>
				{@render WebAndAmenities()}
			</div>
		{/if}
	</div>

	{#snippet OverviewAndStats()}
		{const overviewAndStats = $derived.by<{ label: string; value: string }[]>(() => {
			const items: { label: string; value: string }[] = [];
			if (place.cost) {
				items.push({ label: 'Possible Cost', value: formatCurrency(place.cost.amount, place.cost.currencyCode) });
			}
			if (place.inceptionYear !== null) {
				const age = new Date().getFullYear() - place.inceptionYear;
				items.push({ label: 'Established', value: `In ${place.inceptionYear.toString()}, making it ${age} years old` });
			}
			if (place.visitorsPerYear !== null) {
				items.push({
					label: 'Annual Visitors',
					value: `${place.visitorsPerYear.toLocaleString()}${place.visitorsPerYearAsOf ? ` (${place.visitorsPerYearAsOf})` : ''}`
				});
			}
			if (place.exhibitionSize !== null) {
				items.push({ label: 'Exhibition Items', value: place.exhibitionSize.toLocaleString() });
			}
			if (place.elevationMeters !== null) {
				items.push({ label: 'Elevation', value: `${place.elevationMeters.toLocaleString()} m` });
			}
			if (place.wifiAccess) {
				items.push({ label: 'Wi-Fi', value: WIFI_ACCESS_LABELS[place.wifiAccess] });
			}
			return items;
		})}
		{#each overviewAndStats as { label, value }}
			<div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
				<dt class="text-base-content/70">{label}</dt>
				<dd class="text-right font-semibold text-base-content">{value}</dd>
			</div>
		{/each}
	{/snippet}

	{#snippet ArchitectureAndHeritage()}
		{const architectureAndHeritage = $derived.by<{ label: string; values: string[] }[]>(() => {
			const items: { label: string; values: string[] }[] = [];
			if (place.heritageDesignations && place.heritageDesignations.length > 0) {
				items.push({ label: 'Heritage Status', values: place.heritageDesignations.map(capitalizeWords) });
			}
			if (place.architecturalStyles && place.architecturalStyles.length > 0) {
				items.push({ label: 'Architectural Styles', values: place.architecturalStyles.map(capitalizeWords) });
			}
			if (place.architects && place.architects.length > 0) {
				items.push({ label: 'Architects', values: place.architects.map(capitalizeWords) });
			}
			if (place.founders && place.founders.length > 0) {
				items.push({ label: 'Founders', values: place.founders.map(capitalizeWords) });
			}
			return items;
		})}
		{#each architectureAndHeritage as { label, values }}
			<div class="flex flex-col gap-1.5">
				<dt class="text-base-content/70">{label}</dt>
				<dd class="flex flex-wrap gap-1.5">
					{#each values as value}
						<span class="badge h-auto min-h-5 px-2 py-1 text-left text-xs leading-tight whitespace-normal badge-neutral">
							{capitalizeWords(value)}
						</span>
					{/each}
				</dd>
			</div>
		{/each}
	{/snippet}

	{#snippet WebAndAmenities()}
		{const hasFacilities = place.facilities && place.facilities.length > 0}
		{const hasAccessibilityProperties = place.accessibilityProperties && place.accessibilityProperties.length > 0}
		{#if officialLinks.length > 0}
			<div
				class={{
					'flex flex-wrap gap-2 pb-2.5': true,
					'border-b border-base-content/5': hasFacilities || hasAccessibilityProperties
				}}
			>
				{#each officialLinks as props (props.text)}
					{@render OfficialLinkBtn({ props, btnSizeClass: 'btn-xs' })}
				{/each}
			</div>
		{/if}

		{#if hasFacilities}
			<div class="flex flex-col gap-1.5">
				<span class="text-xs text-base-content/70">Facilities</span>
				<div class="flex flex-wrap gap-1.5">
					{#each place.facilities as facility}
						<span class="badge h-auto min-h-5 badge-soft px-2 py-1 text-left text-xs leading-tight whitespace-normal badge-info">
							{capitalizeWords(facility)}
						</span>
					{/each}
				</div>
			</div>
		{/if}

		{#if hasAccessibilityProperties}
			<div class="flex flex-col gap-1.5 pt-1">
				<span class="text-xs text-base-content/70">Accessibility</span>
				<div class="flex flex-wrap gap-1.5">
					{#each place.accessibilityProperties as property}
						<span
							class="badge h-auto min-h-5 badge-soft px-2 py-1 text-left text-xs leading-tight whitespace-normal badge-success"
						>
							{capitalizeWords(property)}
						</span>
					{/each}
				</div>
			</div>
		{/if}
	{/snippet}
{/snippet}
